import type { SQLiteDatabase } from 'expo-sqlite';

/**
 * Schema versions:
 *   1 — initial: property, system, task, task_completion
 *   2 — property structural details + system subtype/nickname + task tier/icon
 *   3 — document + contact tables; cost/vendor on completions
 *   4 — system seasonality: is_active + active_start_month + active_end_month
 *   5 — custom tasks (is_custom + cadence_days) + notifications prefs
 *   6 — property background photo + display name + accent
 *   7 — custom kinds: system.custom_label, custom_icon, custom_category
 *   8 — inventory_item + notification frequency + overdue reminders
 *   9 — inventory_item.extra_photos_json (multi-photo per item)
 *  10 — expense table (utilities, insurance, taxes, recurring bills)
 *  11 — UI preferences (show_weather, etc.)
 *  12 — show_preloader preference
 *  13 — property address + lat/lon for map preview
 *  14 — lifestyle attributes (pool, solar, pets, kids) for richer task targeting
 *  15 — system.extra_photos_json for multi-photo galleries on systems
 *  16 — goals table (dream board: future home goals with target dates + cost)
 *  17 — goal.extra_photos_json + goal.linked_task_ids_json (project tracker)
 *  18 — property.quick_toolbar_keys_json (user-curated My Home quick actions)
 *  19 — inventory photo captions + per-system standalone reminder
 *  20 — system_reminder child table (multiple reminders per system)
 *  21 — lifestyle expansion: HOA, wood-fire, smart-home, str-host, snow, garden, accessibility, first-timer
 *  22 — owner first/last name for personalized greeting
 *  23 — ask_expense_cost toggle (opt out of the "what did this cost?" prompt
 *       on task completion)
 *  24 — quick_actions_keys_json: user-curated order/visibility for the
 *       Quick Actions sheet that opens from the + FAB
 *  25 — greeting_source: 'first_name' | 'home_name' — which one fills
 *       the Greeting card on the home screen
 */
export const DATABASE_VERSION = 48;
export const DATABASE_NAME = 'homestead.db';

/**
 * Run an ALTER TABLE statement that's safe to re-run if the column already
 * exists. SQLite ADD COLUMN has no IF NOT EXISTS, so we swallow that single
 * benign error and let everything else propagate.
 */
async function safeAlter(db: SQLiteDatabase, sql: string): Promise<void> {
  try {
    await db.execAsync(sql);
  } catch (err) {
    const msg = String(err);
    if (
      msg.includes('duplicate column name') ||
      msg.includes('already exists') ||
      // Tolerate "no such table" too — the additive-ensure pass runs
      // unconditionally and the table may not exist yet on a fresh DB
      // (the version-gated CREATE TABLE block will handle it).
      msg.includes('no such table')
    ) {
      return;
    }
    throw err;
  }
}

/**
 * Idempotent column ensures. Runs on EVERY cold start regardless of the
 * recorded user_version — partial migrations on web (OPFS race conditions,
 * a previous version-bump that aborted mid-ALTER, etc.) can leave the
 * `PRAGMA user_version` ahead of the real table shape. Re-running these
 * is cheap because `safeAlter` swallows "duplicate column name" errors.
 *
 * Add new additive columns here as well as in their version-gated block.
 */
async function ensureAdditiveColumns(db: SQLiteDatabase): Promise<void> {
  // v11
  await safeAlter(db, `ALTER TABLE property ADD COLUMN show_weather INTEGER NOT NULL DEFAULT 1;`);
  await safeAlter(db, `ALTER TABLE property ADD COLUMN confetti_enabled INTEGER NOT NULL DEFAULT 1;`);
  await safeAlter(db, `ALTER TABLE property ADD COLUMN show_quick_toolbar INTEGER NOT NULL DEFAULT 1;`);
  // v12
  await safeAlter(db, `ALTER TABLE property ADD COLUMN show_preloader INTEGER NOT NULL DEFAULT 1;`);
  // v13
  await safeAlter(db, `ALTER TABLE property ADD COLUMN address TEXT;`);
  await safeAlter(db, `ALTER TABLE property ADD COLUMN lat REAL;`);
  await safeAlter(db, `ALTER TABLE property ADD COLUMN lon REAL;`);
  // v14
  await safeAlter(db, `ALTER TABLE property ADD COLUMN has_pool INTEGER NOT NULL DEFAULT 0;`);
  await safeAlter(db, `ALTER TABLE property ADD COLUMN has_solar INTEGER NOT NULL DEFAULT 0;`);
  await safeAlter(db, `ALTER TABLE property ADD COLUMN has_pets INTEGER NOT NULL DEFAULT 0;`);
  await safeAlter(db, `ALTER TABLE property ADD COLUMN has_kids INTEGER NOT NULL DEFAULT 0;`);
  // v15
  await safeAlter(db, `ALTER TABLE system ADD COLUMN extra_photos_json TEXT;`);
  // v17
  await safeAlter(db, `ALTER TABLE goal ADD COLUMN extra_photos_json TEXT;`);
  await safeAlter(db, `ALTER TABLE goal ADD COLUMN linked_task_ids_json TEXT;`);
  // v18
  await safeAlter(db, `ALTER TABLE property ADD COLUMN quick_toolbar_keys_json TEXT;`);
  // v19
  await safeAlter(db, `ALTER TABLE inventory_item ADD COLUMN photo_captions_json TEXT;`);
  await safeAlter(db, `ALTER TABLE system ADD COLUMN reminder_at INTEGER;`);
  await safeAlter(db, `ALTER TABLE system ADD COLUMN reminder_label TEXT;`);
  // v21 lifestyle additions
  await safeAlter(db, `ALTER TABLE property ADD COLUMN has_hoa INTEGER NOT NULL DEFAULT 0;`);
  await safeAlter(db, `ALTER TABLE property ADD COLUMN has_woodfire INTEGER NOT NULL DEFAULT 0;`);
  await safeAlter(db, `ALTER TABLE property ADD COLUMN has_smart_home INTEGER NOT NULL DEFAULT 0;`);
  await safeAlter(db, `ALTER TABLE property ADD COLUMN has_str_host INTEGER NOT NULL DEFAULT 0;`);
  await safeAlter(db, `ALTER TABLE property ADD COLUMN has_heavy_snow INTEGER NOT NULL DEFAULT 0;`);
  await safeAlter(db, `ALTER TABLE property ADD COLUMN has_garden INTEGER NOT NULL DEFAULT 0;`);
  await safeAlter(db, `ALTER TABLE property ADD COLUMN has_accessibility INTEGER NOT NULL DEFAULT 0;`);
  await safeAlter(db, `ALTER TABLE property ADD COLUMN is_first_time INTEGER NOT NULL DEFAULT 0;`);
  // v22 owner name
  await safeAlter(db, `ALTER TABLE property ADD COLUMN owner_first_name TEXT;`);
  await safeAlter(db, `ALTER TABLE property ADD COLUMN owner_last_name TEXT;`);
  // v23 expense-cost prompt opt-out
  await safeAlter(db, `ALTER TABLE property ADD COLUMN ask_expense_cost INTEGER NOT NULL DEFAULT 1;`);
  // v24 user-curated Quick Actions sheet order
  await safeAlter(db, `ALTER TABLE property ADD COLUMN quick_actions_keys_json TEXT;`);
  // v25 greeting source preference
  await safeAlter(db, `ALTER TABLE property ADD COLUMN greeting_source TEXT NOT NULL DEFAULT 'first_name';`);
  // v26 done vs skipped distinction in task history
  await safeAlter(db, `ALTER TABLE task_completion ADD COLUMN kind TEXT NOT NULL DEFAULT 'done';`);
  // v27 desktop sidebar widget visibility
  await safeAlter(db, `ALTER TABLE property ADD COLUMN sidebar_widgets_json TEXT;`);
  // v28 notification ticker visibility
  await safeAlter(db, `ALTER TABLE property ADD COLUMN show_message_ticker INTEGER NOT NULL DEFAULT 1;`);
  // v30 per-category achievement push opt-out
  await safeAlter(db, `ALTER TABLE property ADD COLUMN notify_achievements INTEGER NOT NULL DEFAULT 1;`);
  // v31 global gates + sound master + quiet hours window
  await safeAlter(db, `ALTER TABLE property ADD COLUMN notify_task_reminders INTEGER NOT NULL DEFAULT 1;`);
  await safeAlter(db, `ALTER TABLE property ADD COLUMN notify_system_reminders INTEGER NOT NULL DEFAULT 1;`);
  await safeAlter(db, `ALTER TABLE property ADD COLUMN notify_sound INTEGER NOT NULL DEFAULT 1;`);
  await safeAlter(db, `ALTER TABLE property ADD COLUMN quiet_hours_start INTEGER;`);
  await safeAlter(db, `ALTER TABLE property ADD COLUMN quiet_hours_end INTEGER;`);
  // v32 multi-value structural attributes + message CTAs
  await safeAlter(db, `ALTER TABLE property ADD COLUMN roof_materials_json TEXT;`);
  await safeAlter(db, `ALTER TABLE property ADD COLUMN exterior_materials_json TEXT;`);
  await safeAlter(db, `ALTER TABLE property ADD COLUMN foundations_json TEXT;`);
  // Table is `message` (singular). Earlier code said `messages` which
  // silently no-op'd in safeAlter — meaning the welcome card's CTA
  // route never got persisted and the seed INSERT failed for new users.
  await safeAlter(db, `ALTER TABLE message ADD COLUMN cta_route TEXT;`);
  // v39 — multi-home inbox tag. NULL means "user-wide" (welcome / app
  // info); a real property id means the message is from a specific home.
  await safeAlter(db, `ALTER TABLE message ADD COLUMN property_id TEXT;`);
  // v33 — specific year-built input (collected on the home-type onboarding
  // step). Stored as a plain integer (e.g. 1998). The legacy year_band
  // bucket stays alongside for back-compat and gets derived from this when
  // present. Profile completion now counts year_built instead of year_band.
  await safeAlter(db, `ALTER TABLE property ADD COLUMN year_built INTEGER;`);
  // v34 — persistent task reminders. Stored on the task row so reminders
  // survive across sessions and platforms. On native, the OS notification
  // is the primary delivery mechanism; on web, an app-focus checker reads
  // these columns and fires via service-worker showNotification when due.
  await safeAlter(db, `ALTER TABLE task ADD COLUMN reminder_at INTEGER;`);
  await safeAlter(db, `ALTER TABLE task ADD COLUMN reminder_lead_days INTEGER;`);
  await safeAlter(db, `ALTER TABLE task ADD COLUMN reminder_notified_at INTEGER;`);
  // v35 — 8 new lifestyle flags + 3 property attributes from the catalog
  // review. Defaults are 0 (off) so existing rows are unaffected. The
  // attribute rules library will gate new tasks on these.
  await safeAlter(db, `ALTER TABLE property ADD COLUMN travels_often INTEGER NOT NULL DEFAULT 0;`);
  await safeAlter(db, `ALTER TABLE property ADD COLUMN works_from_home INTEGER NOT NULL DEFAULT 0;`);
  await safeAlter(db, `ALTER TABLE property ADD COLUMN has_allergies INTEGER NOT NULL DEFAULT 0;`);
  await safeAlter(db, `ALTER TABLE property ADD COLUMN has_newborn INTEGER NOT NULL DEFAULT 0;`);
  await safeAlter(db, `ALTER TABLE property ADD COLUMN multi_gen_household INTEGER NOT NULL DEFAULT 0;`);
  await safeAlter(db, `ALTER TABLE property ADD COLUMN over_60_owner INTEGER NOT NULL DEFAULT 0;`);
  await safeAlter(db, `ALTER TABLE property ADD COLUMN has_livestock INTEGER NOT NULL DEFAULT 0;`);
  await safeAlter(db, `ALTER TABLE property ADD COLUMN has_short_term_contractors INTEGER NOT NULL DEFAULT 0;`);
  // Property attribute additions captured during the catalog review.
  //   heating_fuel: 'gas' | 'electric' | 'oil' | 'propane' | 'dual-fuel' | 'unsure'
  //   owned_since: year the user took ownership (drives 'fresh owner' vs 'long-term' nudge tone)
  //   region_hazards_json: JSON array of climate hazards (hurricane, tornado, wildfire, earthquake, flood, ice-storm)
  await safeAlter(db, `ALTER TABLE property ADD COLUMN heating_fuel TEXT;`);
  await safeAlter(db, `ALTER TABLE property ADD COLUMN owned_since INTEGER;`);
  await safeAlter(db, `ALTER TABLE property ADD COLUMN region_hazards_json TEXT;`);
  // v36 — Locations module: capture where each emergency-critical
  // system lives in the home plus a one-liner on how to operate it.
  // Powers the Emergency Card PDF export (works when the phone is dead).
  await safeAlter(db, `ALTER TABLE system ADD COLUMN location_photo_uri TEXT;`);
  await safeAlter(db, `ALTER TABLE system ADD COLUMN location_note TEXT;`);
  // v37 — split "where in the home" from "how to operate" so the printed
  // card reads cleaner and the list rows can show a short location label.
  await safeAlter(db, `ALTER TABLE system ADD COLUMN location_where TEXT;`);
  // v41 — Today-page visibility toggles. Users who already know what
  // they want on the homepage can hide the Quick toolbar (4-tile row)
  // and the Daily calendar peek. Both default ON so nothing changes
  // for existing users without an explicit opt-out.
  await safeAlter(db, `ALTER TABLE property ADD COLUMN show_quick_toolbar INTEGER NOT NULL DEFAULT 1;`);
  await safeAlter(db, `ALTER TABLE property ADD COLUMN show_daily_calendar INTEGER NOT NULL DEFAULT 1;`);
  // Wave 61 — hide the "What you're building toward" projects peek on
  // Today (user-requested preference), and user-defined expense
  // categories (JSON array of strings) merged into the built-in list.
  await safeAlter(db, `ALTER TABLE property ADD COLUMN show_projects_on_today INTEGER NOT NULL DEFAULT 1;`);
  await safeAlter(db, `ALTER TABLE property ADD COLUMN custom_expense_categories_json TEXT;`);
  // Wave 62 — user-curated Tools sheet grid (hamburger menu), same
  // contract as quick_actions_keys_json.
  await safeAlter(db, `ALTER TABLE property ADD COLUMN tools_menu_keys_json TEXT;`);
  // Wave 72 — user profile contact fields (surface on /account; feed
  // future exports + the eventual cloud account).
  await safeAlter(db, `ALTER TABLE property ADD COLUMN owner_email TEXT;`);
  await safeAlter(db, `ALTER TABLE property ADD COLUMN owner_phone TEXT;`);
  // Wave 74 heal: Tune's year_built save historically forgot to refresh
  // the coarse year_band, leaving homes labeled with their old era.
  // Re-derive on every boot for any row where the two disagree —
  // idempotent, and a no-op once consistent.
  await db.execAsync(`
    UPDATE property SET year_band = CASE
      WHEN year_built < 1960 THEN 'pre60'
      WHEN year_built < 1990 THEN '60-80'
      WHEN year_built < 2010 THEN '90-00'
      ELSE '2010+'
    END
    WHERE year_built IS NOT NULL AND year_band != CASE
      WHEN year_built < 1960 THEN 'pre60'
      WHEN year_built < 1990 THEN '60-80'
      WHEN year_built < 2010 THEN '90-00'
      ELSE '2010+'
    END;
  `).catch(() => {});
  // v42 — hemisphere for international support. 'northern' default.
  await safeAlter(db, `ALTER TABLE property ADD COLUMN hemisphere TEXT NOT NULL DEFAULT 'northern';`);
  // v43 — needs_replan flag. Set to 1 when the user changes a
  // planner-affecting field; cleared to 0 after they hit Replan.
  // Powers the "Replan" icon badge on My Home so users know when
  // their schedule is out of date with their home profile.
  await safeAlter(db, `ALTER TABLE property ADD COLUMN needs_replan INTEGER NOT NULL DEFAULT 0;`);
  // v48 — scanned business card image linked to a contact.
  await safeAlter(db, `ALTER TABLE contact ADD COLUMN card_doc_id TEXT REFERENCES document(id) ON DELETE SET NULL;`);
  // v29 persisted message inbox — CREATE TABLE IF NOT EXISTS is safe to
  // re-run on every cold start, same self-healing principle as the column
  // ensures above. Guards against PRAGMA user_version racing ahead of the
  // real schema (which we've seen happen on web during OPFS handoffs).
  await db.execAsync(`
    CREATE TABLE IF NOT EXISTS message (
      id            TEXT PRIMARY KEY NOT NULL,
      title         TEXT NOT NULL,
      body          TEXT,
      ts            INTEGER NOT NULL,
      read          INTEGER NOT NULL DEFAULT 0,
      tone          TEXT NOT NULL,
      ticker_hidden INTEGER NOT NULL DEFAULT 0,
      seed_key      TEXT UNIQUE,
      property_id   TEXT,
      deleted_at    INTEGER,
      created_at    INTEGER NOT NULL,
      updated_at    INTEGER NOT NULL
    );
    CREATE INDEX IF NOT EXISTS idx_message_deleted_at ON message(deleted_at);
    CREATE INDEX IF NOT EXISTS idx_message_ts ON message(ts);
    CREATE INDEX IF NOT EXISTS idx_message_read ON message(read);
  `);
}

/**
 * Wrap migrate() with backoff retry. The OPFS "NoModificationAllowedError"
 * happens on web when another tab/worker still holds the same DB file —
 * usually a stale tab from a hot reload. Three attempts with growing
 * delays is enough to ride out the typical handoff; persistent failures
 * surface to the SQLiteProvider's onError where we render a clear UI.
 */
export async function migrateWithRetry(db: SQLiteDatabase): Promise<void> {
  const delays = [0, 300, 800];
  let lastErr: unknown = null;
  for (const ms of delays) {
    if (ms > 0) await new Promise((r) => setTimeout(r, ms));
    try {
      await migrate(db);
      return;
    } catch (err) {
      lastErr = err;
      const msg = err instanceof Error ? err.message : String(err);
      // Only retry the specific transient OPFS handle conflict — any other
      // failure (schema bug, disk full) should fail fast.
      if (!msg.includes('NoModificationAllowedError') && !msg.includes('Access Handle')) {
        throw err;
      }
    }
  }
  throw lastErr ?? new Error('migrate failed after retries');
}

export async function migrate(db: SQLiteDatabase): Promise<void> {
  // Per-connection pragma, NOT persisted with the database file — it
  // must run on every open, not just inside the v1 bootstrap block.
  // Without it, every ON DELETE CASCADE in the schema is inert after
  // the first launch.
  await db.execAsync('PRAGMA foreign_keys = ON;');

  const result = await db.getFirstAsync<{ user_version: number }>(
    'PRAGMA user_version'
  );
  let version = result?.user_version ?? 0;

  // Up-to-date DB: still run the additive-column ensure so we self-heal any
  // partial migration that left columns missing under a current user_version.
  if (version >= DATABASE_VERSION) {
    await ensureAdditiveColumns(db);
    return;
  }

  if (version < 1) {
    await db.execAsync(`
      PRAGMA journal_mode = WAL;
      PRAGMA foreign_keys = ON;

      CREATE TABLE IF NOT EXISTS property (
        id            TEXT PRIMARY KEY NOT NULL,
        zip           TEXT,
        climate_zone  TEXT,
        home_type     TEXT,
        year_band     TEXT,
        sqft          INTEGER,
        hvac_type     TEXT,
        lot_band      TEXT,
        setup_mode    TEXT NOT NULL DEFAULT 'quick',
        setup_complete INTEGER NOT NULL DEFAULT 0,
        last_replan_at INTEGER,
        created_at    INTEGER NOT NULL,
        updated_at    INTEGER NOT NULL,
        deleted_at    INTEGER
      );

      CREATE TABLE IF NOT EXISTS system (
        id           TEXT PRIMARY KEY NOT NULL,
        property_id  TEXT NOT NULL REFERENCES property(id) ON DELETE CASCADE,
        kind         TEXT NOT NULL,
        location     TEXT NOT NULL CHECK (location IN ('inside','outside')),
        model        TEXT,
        filter_size  TEXT,
        installed_year INTEGER,
        photo_uri    TEXT,
        notes        TEXT,
        created_at   INTEGER NOT NULL,
        updated_at   INTEGER NOT NULL,
        deleted_at   INTEGER
      );
      CREATE INDEX IF NOT EXISTS idx_system_property ON system(property_id);

      CREATE TABLE IF NOT EXISTS task (
        id           TEXT PRIMARY KEY NOT NULL,
        property_id  TEXT NOT NULL REFERENCES property(id) ON DELETE CASCADE,
        system_id    TEXT REFERENCES system(id) ON DELETE SET NULL,
        rule_key     TEXT NOT NULL,
        title        TEXT NOT NULL,
        why          TEXT,
        time_minutes INTEGER,
        difficulty   TEXT CHECK (difficulty IN ('easy','moderate','hard')),
        season       TEXT CHECK (season IN ('spring','summer','fall','winter','any')),
        due_at       INTEGER,
        snoozed_until INTEGER,
        completed_at INTEGER,
        created_at   INTEGER NOT NULL,
        updated_at   INTEGER NOT NULL,
        deleted_at   INTEGER
      );
      CREATE INDEX IF NOT EXISTS idx_task_property_due
        ON task(property_id, completed_at, due_at);

      CREATE TABLE IF NOT EXISTS task_completion (
        id           TEXT PRIMARY KEY NOT NULL,
        task_id      TEXT NOT NULL REFERENCES task(id) ON DELETE CASCADE,
        completed_at INTEGER NOT NULL,
        notes        TEXT,
        created_at   INTEGER NOT NULL,
        updated_at   INTEGER NOT NULL
      );
      CREATE INDEX IF NOT EXISTS idx_completion_task ON task_completion(task_id);
    `);
    version = 1;
  }

  if (version < 2) {
    await safeAlter(db, `ALTER TABLE property ADD COLUMN name TEXT;`);
    await safeAlter(db, `ALTER TABLE property ADD COLUMN stories INTEGER;`);
    await safeAlter(db, `ALTER TABLE property ADD COLUMN bedrooms INTEGER;`);
    await safeAlter(db, `ALTER TABLE property ADD COLUMN bathrooms REAL;`);
    await safeAlter(db, `ALTER TABLE property ADD COLUMN basement TEXT;`);
    await safeAlter(db, `ALTER TABLE property ADD COLUMN garage TEXT;`);
    await safeAlter(db, `ALTER TABLE property ADD COLUMN water_source TEXT;`);
    await safeAlter(db, `ALTER TABLE property ADD COLUMN sewer TEXT;`);
    await safeAlter(db, `ALTER TABLE property ADD COLUMN roof_material TEXT;`);
    await safeAlter(db, `ALTER TABLE property ADD COLUMN foundation TEXT;`);
    await safeAlter(db, `ALTER TABLE property ADD COLUMN exterior_material TEXT;`);
    await safeAlter(db, `ALTER TABLE property ADD COLUMN secondary_hvac_type TEXT;`);

    await safeAlter(db, `ALTER TABLE system ADD COLUMN subtype TEXT;`);
    await safeAlter(db, `ALTER TABLE system ADD COLUMN nickname TEXT;`);

    await safeAlter(db, `ALTER TABLE task ADD COLUMN tier TEXT;`);
    await safeAlter(db, `ALTER TABLE task ADD COLUMN icon TEXT;`);
    version = 2;
  }

  if (version < 3) {
    await db.execAsync(`
      CREATE TABLE IF NOT EXISTS document (
        id           TEXT PRIMARY KEY NOT NULL,
        property_id  TEXT NOT NULL REFERENCES property(id) ON DELETE CASCADE,
        system_id    TEXT REFERENCES system(id) ON DELETE SET NULL,
        task_id      TEXT REFERENCES task(id) ON DELETE SET NULL,
        kind         TEXT NOT NULL,
        title        TEXT NOT NULL,
        file_uri     TEXT NOT NULL,
        remote_url   TEXT,
        mime_type    TEXT,
        file_size    INTEGER,
        expires_at   INTEGER,
        vendor       TEXT,
        cost_cents   INTEGER,
        notes        TEXT,
        created_at   INTEGER NOT NULL,
        updated_at   INTEGER NOT NULL,
        deleted_at   INTEGER
      );
      CREATE INDEX IF NOT EXISTS idx_document_property ON document(property_id);
      CREATE INDEX IF NOT EXISTS idx_document_system ON document(system_id);

      CREATE TABLE IF NOT EXISTS contact (
        id           TEXT PRIMARY KEY NOT NULL,
        property_id  TEXT NOT NULL REFERENCES property(id) ON DELETE CASCADE,
        name         TEXT NOT NULL,
        role         TEXT,
        company      TEXT,
        phone        TEXT,
        email        TEXT,
        website      TEXT,
        notes        TEXT,
        last_used_at INTEGER,
        created_at   INTEGER NOT NULL,
        updated_at   INTEGER NOT NULL,
        deleted_at   INTEGER
      );
      CREATE INDEX IF NOT EXISTS idx_contact_property ON contact(property_id);
    `);
    await safeAlter(db, `ALTER TABLE task_completion ADD COLUMN cost_cents INTEGER;`);
    await safeAlter(db, `ALTER TABLE task_completion ADD COLUMN vendor TEXT;`);
    await safeAlter(
      db,
      `ALTER TABLE task_completion ADD COLUMN contact_id TEXT REFERENCES contact(id) ON DELETE SET NULL;`
    );
    version = 3;
  }

  if (version < 4) {
    await safeAlter(db, `ALTER TABLE system ADD COLUMN is_active INTEGER NOT NULL DEFAULT 1;`);
    await safeAlter(db, `ALTER TABLE system ADD COLUMN active_start_month INTEGER;`);
    await safeAlter(db, `ALTER TABLE system ADD COLUMN active_end_month INTEGER;`);
    version = 4;
  }

  if (version < 5) {
    await safeAlter(db, `ALTER TABLE task ADD COLUMN is_custom INTEGER NOT NULL DEFAULT 0;`);
    await safeAlter(db, `ALTER TABLE task ADD COLUMN cadence_days INTEGER;`);
    await safeAlter(db, `ALTER TABLE task ADD COLUMN supplies_json TEXT;`);
    await safeAlter(db, `ALTER TABLE task ADD COLUMN steps_json TEXT;`);
    await safeAlter(db, `ALTER TABLE task ADD COLUMN tips_json TEXT;`);
    await safeAlter(
      db,
      `ALTER TABLE property ADD COLUMN notifications_enabled INTEGER NOT NULL DEFAULT 0;`
    );
    await safeAlter(
      db,
      `ALTER TABLE property ADD COLUMN notification_day INTEGER NOT NULL DEFAULT 1;`
    );
    await safeAlter(
      db,
      `ALTER TABLE property ADD COLUMN notification_hour INTEGER NOT NULL DEFAULT 8;`
    );
    version = 5;
  }

  if (version < 6) {
    await safeAlter(db, `ALTER TABLE property ADD COLUMN background_uri TEXT;`);
    await safeAlter(db, `ALTER TABLE property ADD COLUMN accent_color TEXT;`);
    version = 6;
  }

  if (version < 7) {
    await safeAlter(db, `ALTER TABLE system ADD COLUMN custom_label TEXT;`);
    await safeAlter(db, `ALTER TABLE system ADD COLUMN custom_icon TEXT;`);
    await safeAlter(db, `ALTER TABLE system ADD COLUMN custom_category TEXT;`);
    version = 7;
  }

  if (version < 8) {
    await db.execAsync(`
      CREATE TABLE IF NOT EXISTS inventory_item (
        id              TEXT PRIMARY KEY NOT NULL,
        property_id     TEXT NOT NULL REFERENCES property(id) ON DELETE CASCADE,
        system_id       TEXT REFERENCES system(id) ON DELETE SET NULL,
        room            TEXT,
        name            TEXT NOT NULL,
        category        TEXT,
        brand           TEXT,
        model           TEXT,
        serial_number   TEXT,
        purchase_date   INTEGER,
        purchase_cents  INTEGER,
        value_cents     INTEGER,
        condition       TEXT,
        vendor          TEXT,
        warranty_until  INTEGER,
        is_insured      INTEGER NOT NULL DEFAULT 0,
        photo_uri       TEXT,
        receipt_doc_id  TEXT REFERENCES document(id) ON DELETE SET NULL,
        warranty_doc_id TEXT REFERENCES document(id) ON DELETE SET NULL,
        notes           TEXT,
        created_at      INTEGER NOT NULL,
        updated_at      INTEGER NOT NULL,
        deleted_at      INTEGER
      );
      CREATE INDEX IF NOT EXISTS idx_inventory_property ON inventory_item(property_id);
      CREATE INDEX IF NOT EXISTS idx_inventory_category ON inventory_item(category);
      CREATE INDEX IF NOT EXISTS idx_inventory_room ON inventory_item(room);
    `);
    await safeAlter(
      db,
      `ALTER TABLE property ADD COLUMN notification_frequency TEXT NOT NULL DEFAULT 'weekly';`
    );
    await safeAlter(
      db,
      `ALTER TABLE property ADD COLUMN notify_overdue INTEGER NOT NULL DEFAULT 1;`
    );
    version = 8;
  }

  if (version < 9) {
    await safeAlter(db, `ALTER TABLE inventory_item ADD COLUMN extra_photos_json TEXT;`);
    version = 9;
  }

  if (version < 11) {
    // Will run after the v10 block below since both are gated by version < N.
  }

  if (version < 10) {
    await db.execAsync(`
      CREATE TABLE IF NOT EXISTS expense (
        id              TEXT PRIMARY KEY NOT NULL,
        property_id     TEXT NOT NULL REFERENCES property(id) ON DELETE CASCADE,
        system_id       TEXT REFERENCES system(id) ON DELETE SET NULL,
        category        TEXT NOT NULL,
        vendor          TEXT,
        amount_cents    INTEGER NOT NULL,
        paid_on         INTEGER NOT NULL,
        description     TEXT,
        recurring       TEXT,
        tax_deductible  INTEGER NOT NULL DEFAULT 0,
        receipt_doc_id  TEXT REFERENCES document(id) ON DELETE SET NULL,
        notes           TEXT,
        created_at      INTEGER NOT NULL,
        updated_at      INTEGER NOT NULL,
        deleted_at      INTEGER
      );
      CREATE INDEX IF NOT EXISTS idx_expense_property_date
        ON expense(property_id, paid_on);
      CREATE INDEX IF NOT EXISTS idx_expense_category
        ON expense(category);
    `);
    version = 10;
  }

  if (version < 11) {
    await safeAlter(db, `ALTER TABLE property ADD COLUMN show_weather INTEGER NOT NULL DEFAULT 1;`);
    await safeAlter(db, `ALTER TABLE property ADD COLUMN confetti_enabled INTEGER NOT NULL DEFAULT 1;`);
    await safeAlter(db, `ALTER TABLE property ADD COLUMN show_quick_toolbar INTEGER NOT NULL DEFAULT 1;`);
    version = 11;
  }

  if (version < 12) {
    await safeAlter(db, `ALTER TABLE property ADD COLUMN show_preloader INTEGER NOT NULL DEFAULT 1;`);
    version = 12;
  }

  if (version < 13) {
    await safeAlter(db, `ALTER TABLE property ADD COLUMN address TEXT;`);
    await safeAlter(db, `ALTER TABLE property ADD COLUMN lat REAL;`);
    await safeAlter(db, `ALTER TABLE property ADD COLUMN lon REAL;`);
    version = 13;
  }

  if (version < 14) {
    // Lifestyle attributes — drive a second wave of rule targeting beyond
    // structural details. All default to 0 so existing homes don't suddenly
    // sprout pool/solar/pet tasks.
    await safeAlter(db, `ALTER TABLE property ADD COLUMN has_pool INTEGER NOT NULL DEFAULT 0;`);
    await safeAlter(db, `ALTER TABLE property ADD COLUMN has_solar INTEGER NOT NULL DEFAULT 0;`);
    await safeAlter(db, `ALTER TABLE property ADD COLUMN has_pets INTEGER NOT NULL DEFAULT 0;`);
    await safeAlter(db, `ALTER TABLE property ADD COLUMN has_kids INTEGER NOT NULL DEFAULT 0;`);
    version = 14;
  }

  if (version < 15) {
    // Multi-photo gallery on systems (mirror of inventory_item's
    // extra_photos_json). Stored as JSON array of file URIs.
    await safeAlter(db, `ALTER TABLE system ADD COLUMN extra_photos_json TEXT;`);
    version = 15;
  }

  if (version < 16) {
    // Goals / dreams — long-horizon home improvements separate from the
    // task system (which is for recurring upkeep). Sync-ready (UUID,
    // updated_at, soft delete).
    await db.execAsync(`
      CREATE TABLE IF NOT EXISTS goal (
        id              TEXT PRIMARY KEY NOT NULL,
        property_id     TEXT NOT NULL REFERENCES property(id) ON DELETE CASCADE,
        title           TEXT NOT NULL,
        why             TEXT,
        category        TEXT,
        target_date     INTEGER,
        est_cost_cents  INTEGER,
        status          TEXT NOT NULL DEFAULT 'someday',
        priority        INTEGER NOT NULL DEFAULT 2,
        notes           TEXT,
        photo_uri       TEXT,
        created_at      INTEGER NOT NULL,
        updated_at      INTEGER NOT NULL,
        deleted_at      INTEGER
      );
      CREATE INDEX IF NOT EXISTS idx_goal_property ON goal(property_id, status, deleted_at);
    `);
    version = 16;
  }

  if (version < 17) {
    // Goal photo gallery + child-task project tracker. extra_photos_json
    // mirrors the system / inventory pattern; linked_task_ids_json is the
    // simplest possible "goal → tasks" association (a JSON id array on the
    // goal row, since tasks are the higher-volume table and we only need
    // small lists per goal).
    await safeAlter(db, `ALTER TABLE goal ADD COLUMN extra_photos_json TEXT;`);
    await safeAlter(db, `ALTER TABLE goal ADD COLUMN linked_task_ids_json TEXT;`);
    version = 17;
  }

  if (version < 18) {
    // User-curated quick toolbar — JSON array of action keys in order.
    // NULL = default set (all actions in default order).
    await safeAlter(db, `ALTER TABLE property ADD COLUMN quick_toolbar_keys_json TEXT;`);
    version = 18;
  }

  if (version < 19) {
    // Inventory: per-photo captions, keyed by URI in a JSON object so they
    // survive add/remove/reorder of the photo array.
    await safeAlter(db, `ALTER TABLE inventory_item ADD COLUMN photo_captions_json TEXT;`);
    // System: standalone reminder (insurance renewal, warranty expiry,
    // service-contract end). Kept as quick "primary reminder" fields on
    // the system row even after v20 added the multi-reminder table —
    // backward compat for any UI already wired to them.
    await safeAlter(db, `ALTER TABLE system ADD COLUMN reminder_at INTEGER;`);
    await safeAlter(db, `ALTER TABLE system ADD COLUMN reminder_label TEXT;`);
    version = 19;
  }

  if (version < 20) {
    // Multiple reminders per system. Child table keyed by system_id, with
    // its own soft-delete + updated_at for sync-readiness. Notification
    // identifier = `homestead-sysrem-${id}`.
    await db.execAsync(`
      CREATE TABLE IF NOT EXISTS system_reminder (
        id          TEXT PRIMARY KEY NOT NULL,
        system_id   TEXT NOT NULL REFERENCES system(id) ON DELETE CASCADE,
        label       TEXT NOT NULL,
        fire_at     INTEGER NOT NULL,
        created_at  INTEGER NOT NULL,
        updated_at  INTEGER NOT NULL,
        deleted_at  INTEGER
      );
      CREATE INDEX IF NOT EXISTS idx_sysrem_system ON system_reminder(system_id, deleted_at);
    `);
    version = 20;
  }

  if (version < 21) {
    // Second wave of lifestyle attributes — covers the long tail of US
    // homeowner archetypes that the original pool/solar/pets/kids didn't.
    await safeAlter(db, `ALTER TABLE property ADD COLUMN has_hoa INTEGER NOT NULL DEFAULT 0;`);
    await safeAlter(db, `ALTER TABLE property ADD COLUMN has_woodfire INTEGER NOT NULL DEFAULT 0;`);
    await safeAlter(db, `ALTER TABLE property ADD COLUMN has_smart_home INTEGER NOT NULL DEFAULT 0;`);
    await safeAlter(db, `ALTER TABLE property ADD COLUMN has_str_host INTEGER NOT NULL DEFAULT 0;`);
    await safeAlter(db, `ALTER TABLE property ADD COLUMN has_heavy_snow INTEGER NOT NULL DEFAULT 0;`);
    await safeAlter(db, `ALTER TABLE property ADD COLUMN has_garden INTEGER NOT NULL DEFAULT 0;`);
    await safeAlter(db, `ALTER TABLE property ADD COLUMN has_accessibility INTEGER NOT NULL DEFAULT 0;`);
    await safeAlter(db, `ALTER TABLE property ADD COLUMN is_first_time INTEGER NOT NULL DEFAULT 0;`);
    version = 21;
  }

  if (version < 22) {
    await safeAlter(db, `ALTER TABLE property ADD COLUMN owner_first_name TEXT;`);
    await safeAlter(db, `ALTER TABLE property ADD COLUMN owner_last_name TEXT;`);
    version = 22;
  }

  if (version < 23) {
    await safeAlter(db, `ALTER TABLE property ADD COLUMN ask_expense_cost INTEGER NOT NULL DEFAULT 1;`);
    version = 23;
  }

  if (version < 24) {
    await safeAlter(db, `ALTER TABLE property ADD COLUMN quick_actions_keys_json TEXT;`);
    version = 24;
  }

  if (version < 25) {
    await safeAlter(db, `ALTER TABLE property ADD COLUMN greeting_source TEXT NOT NULL DEFAULT 'first_name';`);
    version = 25;
  }

  if (version < 26) {
    // Distinguish "done" from "skipped" in task history. Default 'done'
    // preserves the meaning of every existing completion row.
    await safeAlter(db, `ALTER TABLE task_completion ADD COLUMN kind TEXT NOT NULL DEFAULT 'done';`);
    version = 26;
  }

  if (version < 27) {
    // Desktop sidebar widget visibility (JSON array of widget keys).
    // NULL = use defaults (nickname + map + past_tasks).
    await safeAlter(db, `ALTER TABLE property ADD COLUMN sidebar_widgets_json TEXT;`);
    version = 27;
  }

  if (version < 28) {
    // Notification ticker visibility — defaults to 1 (on).
    await safeAlter(db, `ALTER TABLE property ADD COLUMN show_message_ticker INTEGER NOT NULL DEFAULT 1;`);
    version = 28;
  }

  if (version < 29) {
    // Persisted in-app messages — replaces the in-memory store so unread
    // state, dismissals, and achievement history survive reloads. `seed_key`
    // is UNIQUE so seed messages can be re-inserted idempotently
    // (INSERT OR IGNORE) without resurrecting messages a user already
    // deleted.
    await db.execAsync(`
      CREATE TABLE IF NOT EXISTS message (
        id            TEXT PRIMARY KEY NOT NULL,
        title         TEXT NOT NULL,
        body          TEXT,
        ts            INTEGER NOT NULL,
        read          INTEGER NOT NULL DEFAULT 0,
        tone          TEXT NOT NULL,
        ticker_hidden INTEGER NOT NULL DEFAULT 0,
        seed_key      TEXT UNIQUE,
        deleted_at    INTEGER,
        created_at    INTEGER NOT NULL,
        updated_at    INTEGER NOT NULL
      );
      CREATE INDEX IF NOT EXISTS idx_message_deleted_at ON message(deleted_at);
      CREATE INDEX IF NOT EXISTS idx_message_ts ON message(ts);
      CREATE INDEX IF NOT EXISTS idx_message_read ON message(read);
    `);
    version = 29;
  }

  if (version < 30) {
    // Per-category opt-out for achievement push notifications.
    // Defaults to 1 (on) so existing users see no behavior change
    // until they explicitly turn it off in /notifications.
    await safeAlter(
      db,
      `ALTER TABLE property ADD COLUMN notify_achievements INTEGER NOT NULL DEFAULT 1;`
    );
    version = 30;
  }

  if (version < 31) {
    // Round-out the notification preferences:
    //   notify_task_reminders   — global gate for per-task reminders
    //   notify_system_reminders — global gate for per-system reminders
    //   notify_sound            — master sound on/off for OS notifications
    //   quiet_hours_start       — hour 0-23, NULL when quiet hours off
    //   quiet_hours_end         — hour 0-23, NULL when quiet hours off
    // All four toggles default ON; quiet hours default OFF.
    await safeAlter(
      db,
      `ALTER TABLE property ADD COLUMN notify_task_reminders INTEGER NOT NULL DEFAULT 1;`
    );
    await safeAlter(
      db,
      `ALTER TABLE property ADD COLUMN notify_system_reminders INTEGER NOT NULL DEFAULT 1;`
    );
    await safeAlter(
      db,
      `ALTER TABLE property ADD COLUMN notify_sound INTEGER NOT NULL DEFAULT 1;`
    );
    await safeAlter(
      db,
      `ALTER TABLE property ADD COLUMN quiet_hours_start INTEGER;`
    );
    await safeAlter(
      db,
      `ALTER TABLE property ADD COLUMN quiet_hours_end INTEGER;`
    );
    version = 31;
  }

  // v32 — multi-value structural attributes + message CTAs.
  //
  // Real homes often have mixed siding (brick front + vinyl sides),
  // mixed roof (main asphalt + flat porch addition), or split-level
  // foundations (basement + slab). The original schema forced a
  // single choice per attribute. v32 adds JSON-array columns so users
  // can record every material they actually have.
  //
  // Backward compatibility: the original single-value columns
  // (roof_material, exterior_material, foundation) stay in the
  // schema. The migration seeds the JSON array from the single
  // value where one was set. Read paths prefer the JSON array.
  //
  // Bundled: `messages.cta_route` (TEXT). When set, a message tap
  // routes to that URL instead of just opening the inbox detail —
  // unlocks the "tap to tune my plan" affordance on the welcome
  // message and any future actionable inbox items.
  if (version < 32) {
    await safeAlter(
      db,
      `ALTER TABLE property ADD COLUMN roof_materials_json TEXT;`
    );
    await safeAlter(
      db,
      `ALTER TABLE property ADD COLUMN exterior_materials_json TEXT;`
    );
    await safeAlter(
      db,
      `ALTER TABLE property ADD COLUMN foundations_json TEXT;`
    );
    // Singular table name — see ensureAdditiveColumns comment.
    await safeAlter(
      db,
      `ALTER TABLE message ADD COLUMN cta_route TEXT;`
    );
    // Seed JSON arrays from the existing single-value columns where
    // the user already made a choice — keeps their data intact
    // post-migration. "unknown" / "unsure" stays as null so the
    // multi-select UI shows "nothing picked" instead of an array
    // containing a placeholder.
    await db.execAsync(`
      UPDATE property
      SET roof_materials_json = '["' || roof_material || '"]'
      WHERE roof_material IS NOT NULL
        AND roof_material NOT IN ('unsure', 'unknown')
        AND roof_materials_json IS NULL;
    `);
    await db.execAsync(`
      UPDATE property
      SET exterior_materials_json = '["' || exterior_material || '"]'
      WHERE exterior_material IS NOT NULL
        AND exterior_material NOT IN ('unsure', 'unknown', 'other')
        AND exterior_materials_json IS NULL;
    `);
    await db.execAsync(`
      UPDATE property
      SET foundations_json = '["' || foundation || '"]'
      WHERE foundation IS NOT NULL
        AND foundation NOT IN ('unsure', 'unknown')
        AND foundations_json IS NULL;
    `);
    version = 32;
  }

  // v33 — specific year_built (INTEGER) collected on home-type onboarding
  // step. Sits alongside the existing year_band bucket; year_band can be
  // derived from this when present (see lib/year-band.ts).
  if (version < 33) {
    await safeAlter(db, `ALTER TABLE property ADD COLUMN year_built INTEGER;`);
    version = 33;
  }

  // v34 — persistent task reminders for cross-platform delivery. Native
  // still schedules the OS notification (primary path); web reads these
  // columns on app focus and fires via service-worker showNotification.
  if (version < 34) {
    await safeAlter(db, `ALTER TABLE task ADD COLUMN reminder_at INTEGER;`);
    await safeAlter(db, `ALTER TABLE task ADD COLUMN reminder_lead_days INTEGER;`);
    await safeAlter(db, `ALTER TABLE task ADD COLUMN reminder_notified_at INTEGER;`);
    version = 34;
  }

  // v35 — 8 new lifestyle flags + 3 new property attributes from the
  // catalog-review pass. Lifestyles default to 0 (off); attributes are
  // nullable so existing properties carry no change.
  if (version < 35) {
    await safeAlter(db, `ALTER TABLE property ADD COLUMN travels_often INTEGER NOT NULL DEFAULT 0;`);
    await safeAlter(db, `ALTER TABLE property ADD COLUMN works_from_home INTEGER NOT NULL DEFAULT 0;`);
    await safeAlter(db, `ALTER TABLE property ADD COLUMN has_allergies INTEGER NOT NULL DEFAULT 0;`);
    await safeAlter(db, `ALTER TABLE property ADD COLUMN has_newborn INTEGER NOT NULL DEFAULT 0;`);
    await safeAlter(db, `ALTER TABLE property ADD COLUMN multi_gen_household INTEGER NOT NULL DEFAULT 0;`);
    await safeAlter(db, `ALTER TABLE property ADD COLUMN over_60_owner INTEGER NOT NULL DEFAULT 0;`);
    await safeAlter(db, `ALTER TABLE property ADD COLUMN has_livestock INTEGER NOT NULL DEFAULT 0;`);
    await safeAlter(db, `ALTER TABLE property ADD COLUMN has_short_term_contractors INTEGER NOT NULL DEFAULT 0;`);
    await safeAlter(db, `ALTER TABLE property ADD COLUMN heating_fuel TEXT;`);
    await safeAlter(db, `ALTER TABLE property ADD COLUMN owned_since INTEGER;`);
    await safeAlter(db, `ALTER TABLE property ADD COLUMN region_hazards_json TEXT;`);
    version = 35;
  }

  // Final guard: re-run all additive ensures regardless of how we got here.
  // Belt-and-suspenders against any future partial migration.
  await ensureAdditiveColumns(db);

  // v36 — Locations module: photo + how-to-operate per emergency system.
  // Drives the Emergency Card PDF export.
  if (version < 36) {
    await safeAlter(db, `ALTER TABLE system ADD COLUMN location_photo_uri TEXT;`);
    await safeAlter(db, `ALTER TABLE system ADD COLUMN location_note TEXT;`);
    version = 36;
  }

  // v37 — split where-in-the-home from how-to-operate so the list rows
  // can show a short location and the printed card reads cleaner.
  if (version < 37) {
    await safeAlter(db, `ALTER TABLE system ADD COLUMN location_where TEXT;`);
    version = 37;
  }

  // v38 — multi-home support. Single-row user_settings table holds the
  // active property id (which home the user is currently looking at)
  // and the subscription tier (free = 1 home, paid = up to 5).
  //
  // Why a separate table instead of a column on property: the active
  // selection is a property OF THE USER, not OF A PROPERTY. Stashing
  // it on one of the properties would make the schema awkward (which
  // row holds it?) and force a write to a different row every switch.
  //
  // We seed a default row in the migration so reads never return null
  // and `getActiveProperty` can do a single JOIN-or-fallback query.
  if (version < 38) {
    await db.execAsync(`
      CREATE TABLE IF NOT EXISTS user_settings (
        id TEXT PRIMARY KEY NOT NULL,
        active_property_id TEXT,
        tier TEXT NOT NULL DEFAULT 'paid' CHECK (tier IN ('free', 'paid')),
        updated_at INTEGER NOT NULL
      );
    `);
    // Seed the singleton row if missing. Active property id is left
    // NULL — getActiveProperty falls back to "oldest setup-complete
    // property" in that case, preserving v37 behavior for existing
    // single-home users until they explicitly switch.
    await db.runAsync(
      `INSERT OR IGNORE INTO user_settings (id, active_property_id, tier, updated_at)
       VALUES ('singleton', NULL, 'paid', ?)`,
      Date.now()
    );
    version = 38;
  }

  // v39 — multi-home inbox tagging. Messages can now belong to a
  // specific property so the inbox UI can show "from Lake House" tags
  // and (eventually) filter by home. NULL = user-level message (welcome
  // card, how-it-works, app-wide info) that applies regardless of which
  // home is active.
  if (version < 39) {
    await safeAlter(db, `ALTER TABLE message ADD COLUMN property_id TEXT;`);
    version = 39;
  }

  // v40 — track the last time the planner regenerated this property's
  // task set. Drives the annual "Time to replan" nudge: once it's been
  // ~12 months since the last replan, the Home tab surfaces a chip
  // suggesting the user re-tune the year. Backfilled from created_at
  // so existing homes don't get an immediate nudge on first launch
  // after the migration — they get up to a full year of breathing
  // room before the suggestion fires.
  if (version < 40) {
    await safeAlter(db, `ALTER TABLE property ADD COLUMN last_replan_at INTEGER;`);
    await db.runAsync(
      `UPDATE property SET last_replan_at = created_at WHERE last_replan_at IS NULL`
    );
    version = 40;
  }

  // v41 — Today-page visibility toggles. Lets ADHD-focused users hide
  // sections they don't need (Quick toolbar, Daily calendar peek) so the
  // Today tab can be exactly as dense or as spare as suits them.
  if (version < 41) {
    await safeAlter(db, `ALTER TABLE property ADD COLUMN show_quick_toolbar INTEGER NOT NULL DEFAULT 1;`);
    await safeAlter(db, `ALTER TABLE property ADD COLUMN show_daily_calendar INTEGER NOT NULL DEFAULT 1;`);
    version = 41;
  }

  // v42 — hemisphere for international support. Default 'northern'
  // matches the existing US-built catalog, so no behavior change for
  // current users. Southern-hemisphere homes (Australia, NZ, southern
  // Africa, South America) flip season anchors so a "spring" rule fires
  // in their Sept-Nov instead of our Mar-May.
  if (version < 42) {
    await safeAlter(
      db,
      `ALTER TABLE property ADD COLUMN hemisphere TEXT NOT NULL DEFAULT 'northern';`
    );
    version = 42;
  }

  // v43 — needs_replan flag. Replaces the noisy auto-replan that fired
  // on every Edit Home keystroke. Now any planner-affecting field
  // change just SETS this flag; the user kicks the actual regen via
  // the Replan button (My Home icon, sticky Edit Home bottom bar, or
  // Tools menu). Critical for the upcoming Google Calendar sync —
  // we never want to thrash 60+ calendar events behind the user's back.
  if (version < 43) {
    await safeAlter(
      db,
      `ALTER TABLE property ADD COLUMN needs_replan INTEGER NOT NULL DEFAULT 0;`
    );
    version = 43;
  }

  // v44 — free-form notes per property. Markdown body + optional title;
  // pinned-first ordering. Notes also surface in the Activity timeline
  // alongside completed tasks so the user gets a unified "what happened
  // to this home" view. Soft-delete so cloud sync (deferred) has a clean
  // tombstone strategy.
  if (version < 44) {
    await db.execAsync(`
      CREATE TABLE IF NOT EXISTS note (
        id           TEXT PRIMARY KEY NOT NULL,
        property_id  TEXT NOT NULL REFERENCES property(id) ON DELETE CASCADE,
        title        TEXT,
        body         TEXT NOT NULL,
        pinned       INTEGER NOT NULL DEFAULT 0,
        sort_order   INTEGER,
        created_at   INTEGER NOT NULL,
        updated_at   INTEGER NOT NULL,
        deleted_at   INTEGER
      );
      CREATE INDEX IF NOT EXISTS idx_note_property ON note(property_id);
      CREATE INDEX IF NOT EXISTS idx_note_updated ON note(property_id, updated_at);
    `);
    version = 44;
  }

  // v45 — manual reorder on notes. Pinned still floats to the top, but
  // within the pinned/unpinned groups the user can drag-drop notes into
  // an explicit order on the /notes page. NULL means "fall back to
  // updated_at" so existing notes order naturally on first load. The
  // ALTER runs idempotently via safeAlter for users coming from a
  // pre-Wave-38 v44 install — fresh installs get the column from the
  // CREATE TABLE above.
  //
  // ORDER NOTE (Wave 45): version blocks in this function MUST stay in
  // ascending numeric order. Each block sets `version = N`, so a
  // higher-numbered block placed earlier would make every later
  // lower-numbered check false and silently skip those migrations.
  // (An earlier revision had v46 above this block, which skipped the
  // sort_order ALTER for users upgrading from exactly v44 — the
  // idempotent re-run in ensureAdditiveColumns is the only reason
  // that wasn't a visible break.)
  if (version < 45) {
    await safeAlter(db, `ALTER TABLE note ADD COLUMN sort_order INTEGER;`);
    version = 45;
  }

  // v46 — two unrelated additions, bundled for one migration round-trip:
  //   1. `system.seasonal_confirmed_at` — set when the user explicitly
  //      reviews+saves the open/close months in the Tune walkthrough.
  //      Even when onboarding pre-fills the months (smart defaults
  //      based on climate), we still want to ask the user to confirm
  //      so the cards show in Tune. After confirmation we stop asking.
  //   2. `property.monthly_task_mode` — opt-in. When ON, the planner
  //      normalizes every task's due date to the 1st of its month and
  //      the digest fires once per month on day 1 instead of daily.
  //      Quieter for users who batch all their home stuff into one
  //      "first of the month" session.
  if (version < 46) {
    await safeAlter(db, `ALTER TABLE system ADD COLUMN seasonal_confirmed_at INTEGER;`);
    await safeAlter(
      db,
      `ALTER TABLE property ADD COLUMN monthly_task_mode INTEGER NOT NULL DEFAULT 0;`
    );
    version = 46;
  }

  // v47 — quotes. Homeowners collect contractor estimates for years and
  // want to (a) compare competing bids on one project and (b) look back
  // at what work cost in the past. A quote is "maybe money": it carries
  // the same financial anatomy as an expense (vendor, amount, category,
  // doc scan) plus a lifecycle (open → accepted / declined / expired →
  // converted). Converting to an expense KEEPS the quote row — it becomes
  // the provenance record, linked via converted_expense_id.
  //   project        — free-text label ("Roof replacement") clustering
  //                    competing bids for side-by-side comparison.
  //   contact_id     — who gave the quote; ties into the contacts book.
  //   quote_doc_id   — scanned estimate photo/PDF, same Document pattern
  //                    as expense.receipt_doc_id.
  //   valid_until    — optional expiry ("good for 30 days").
  if (version < 47) {
    await db.execAsync(`
      CREATE TABLE IF NOT EXISTS quote (
        id                    TEXT PRIMARY KEY NOT NULL,
        property_id           TEXT NOT NULL REFERENCES property(id) ON DELETE CASCADE,
        system_id             TEXT REFERENCES system(id) ON DELETE SET NULL,
        contact_id            TEXT REFERENCES contact(id) ON DELETE SET NULL,
        project               TEXT,
        category              TEXT NOT NULL,
        vendor                TEXT,
        description           TEXT,
        amount_cents          INTEGER NOT NULL,
        quoted_on             INTEGER NOT NULL,
        valid_until           INTEGER,
        status                TEXT NOT NULL DEFAULT 'open',
        quote_doc_id          TEXT REFERENCES document(id) ON DELETE SET NULL,
        converted_expense_id  TEXT REFERENCES expense(id) ON DELETE SET NULL,
        notes                 TEXT,
        created_at            INTEGER NOT NULL,
        updated_at            INTEGER NOT NULL,
        deleted_at            INTEGER
      );
      CREATE INDEX IF NOT EXISTS idx_quote_property ON quote(property_id);
      CREATE INDEX IF NOT EXISTS idx_quote_project ON quote(property_id, project);
    `);
    version = 47;
  }

  // v48 (Wave 47): business-card scanning on contacts. The scanned card
  // image persists as a Document; this links it so the contact page can
  // show the card itself.
  if (version < 48) {
    await safeAlter(
      db,
      `ALTER TABLE contact ADD COLUMN card_doc_id TEXT REFERENCES document(id) ON DELETE SET NULL;`
    );
    version = 48;
  }

  await db.execAsync(`PRAGMA user_version = ${DATABASE_VERSION}`);
}
